import { useEffect, useState } from 'react';
import { DevEnvBanner } from 'env-kit/client/index.js';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';
import { I18nProvider, useT, type Lang } from './i18n';
import { api } from './api/client';
import { LoginPage } from './pages/LoginPage';
import { UpcomingPage } from './pages/UpcomingPage';
import { SessionPage } from './pages/SessionPage';
import { AdminPage } from './pages/AdminPage';
import { AppShell } from './components/AppShell';
import { MyParticipantsPage } from './pages/MyParticipantsPage';
import { ParticipantDetailPage } from './pages/ParticipantDetailPage';
import { CardUsagePage } from './pages/CardUsagePage';
import { InsightsPage } from './pages/InsightsPage';

type Route =
  | { name: 'upcoming' }
  | { name: 'session'; sessionId: number }
  | { name: 'participants' }
  | { name: 'participant'; v2Id: number | null; participantName: string | null }
  | { name: 'cards' }
  | { name: 'insights' }
  | { name: 'admin' };

function parseHash(hash: string): Route {
  const raw = hash.replace(/^#/, '') || 'upcoming';
  const [page, ...rest] = raw.split('/');
  if (page === 'admin') return { name: 'admin' };
  if (page === 'cards') return { name: 'cards' };
  if (page === 'insights') return { name: 'insights' };
  if (page === 'session' && rest[0]) {
    const id = parseInt(rest[0], 10);
    if (Number.isFinite(id)) return { name: 'session', sessionId: id };
  }
  if (page === 'participants') return { name: 'participants' };
  if (page === 'participant' && rest[0] === 'v2' && rest[1]) {
    const id = parseInt(rest[1], 10);
    if (Number.isFinite(id)) return { name: 'participant', v2Id: id, participantName: null };
  }
  if (page === 'participant' && rest[0] === 'name' && rest[1]) {
    return { name: 'participant', v2Id: null, participantName: decodeURIComponent(rest.slice(1).join('/')) };
  }
  return { name: 'upcoming' };
}

function AppContent() {
  const { user, loading, isAdmin } = useAuth();
  const { t, setLang } = useT();
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Apply the user's server-stored language preference (canonical, wins over
  // localStorage). Users who never chose keep the local/default value ('fr').
  useEffect(() => {
    if (!user) return;
    api.get<{ lang: Lang | null }>('/api/profile')
      .then((p) => {
        if (p.lang === 'fr' || p.lang === 'en') setLang(p.lang);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const navigate = (page: string) => {
    window.location.hash = page;
  };

  if (loading) {
    return <div className="login-page"><div className="spinner" /></div>;
  }
  if (!user) return <LoginPage />;

  if ((route.name === 'admin' || route.name === 'insights') && !isAdmin) {
    window.location.hash = 'upcoming';
    return null;
  }

  const renderPage = () => {
    if (route.name === 'session') {
      return <SessionPage sessionId={route.sessionId} onBack={() => navigate('upcoming')} />;
    }
    if (route.name === 'admin') return <AdminPage />;
    if (route.name === 'cards') return <CardUsagePage />;
    if (route.name === 'insights') return <InsightsPage />;
    if (route.name === 'participants') {
      return (
        <MyParticipantsPage
          onOpenParticipant={(v2Id, name) =>
            navigate(v2Id != null ? `participant/v2/${v2Id}` : `participant/name/${encodeURIComponent(name)}`)
          }
        />
      );
    }
    if (route.name === 'participant') {
      return (
        <ParticipantDetailPage
          v2Id={route.v2Id}
          participantName={route.participantName}
          onBack={() => navigate('participants')}
        />
      );
    }
    return <UpcomingPage onOpenSession={(id) => navigate(`session/${id}`)} />;
  };

  // touch t so eslint doesn't complain when route.name doesn't reference it directly
  void t;

  return (
    <ThemeProvider>
      <SettingsProvider>
        <AppShell
          isAdmin={isAdmin}
          currentPage={route.name === 'participant' ? 'participants' : route.name}
          onNavigate={navigate}
        >
          {renderPage()}
        </AppShell>
      </SettingsProvider>
    </ThemeProvider>
  );
}

// env-kit's dev banner (DEPLOY_ENV=dev): pinned to the bottom of every screen,
// login page included, pointing people to the real Evolution. Nothing on prod.
function DevBanner() {
  const { t } = useT();
  return <DevEnvBanner labels={{ label: t('shell.devBanner.label'), text: t('shell.devBanner.text') }} />;
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
      <DevBanner />
    </I18nProvider>
  );
}
